// ops-data.js — THE data layer for the cost console, implementing the ops data
// contract (docs/ops/OPS_CONSOLE_CONTRACT.md) exactly. Reads/writes the four
// resource types in the healther-ops Medplum project as the logged-in super-admin
// (RLS is the lock — no service account here), and computes money at read time.
//
// MOCK MODE (?mock=1 or VITE_OPS_MOCK): every fetch/save is served from a
// deterministic in-memory dataset (mock-data.js) so all screens are demonstrable
// with no backend. Saves mutate the in-memory store.
import { getClient } from './medplum.js';
import { createMockDataset, DOCTOR_NAMES } from './mock-data.js';

// ── Contract systems (§1–§4) ────────────────────────────────────────────────
export const SYS = {
  hospital: 'https://healther.io/ops/hospital',
  source: 'https://healther.io/ops/source',
  provider: 'https://healther.io/ops/provider',
  product: 'https://healther.io/ops/product',
  metric: 'https://healther.io/ops/metric',
  usecase: 'https://healther.io/ops/usecase',
  department: 'https://healther.io/ops/department',
  doctor: 'https://healther.io/ops/doctor',
  rollupId: 'https://healther.io/ops/rollup',
};
export const OPS_CODE_SYSTEM = 'https://healther.io/ops';
export const PAYLOAD_EXT = 'https://healther.io/ops/payload';
export const PRICE_URL_PREFIX = 'https://healther.io/ops/price/';
export const PRICE_BASIS_EXT = 'https://healther.io/ops/price-basis';

// ── Mode ─────────────────────────────────────────────────────────────────────
export function isMock() {
  try {
    if (import.meta.env && import.meta.env.VITE_OPS_MOCK) return true;
    const p = new URLSearchParams(window.location.search);
    return p.get('mock') === '1' || p.get('mock') === 'true';
  } catch (_) { return false; }
}

let _mock = null;
function mock() {
  if (!_mock) _mock = createMockDataset();
  return _mock;
}

// id → display-name map. Seeded by the mock dataset, and LEARNED from live
// telemetry: events carry `doctorName` stamped at the call site (contract §4),
// so a new doctor's name appears with their first usage — no roster sync, no
// bot, nothing to go stale. Latest observed name wins (renames self-heal).
const _learnedDoctorNames = new Map();

export function learnDoctorNames(telemetry) {
  for (const t of telemetry || []) {
    if (t?.doctor && t.doctor !== '-' && t.doctorName) _learnedDoctorNames.set(t.doctor, t.doctorName);
  }
}

export function resolveDoctorName(id) {
  if (!id || id === '-') return 'Platform / unattributed';
  return _learnedDoctorNames.get(id) || DOCTOR_NAMES[id] || id;
}

// ── FHIR helpers ─────────────────────────────────────────────────────────────
async function searchAll(resourceType, query) {
  const medplum = await getClient();
  let bundle = await medplum.search(resourceType, query);
  const out = [];
  const collect = (b) => { for (const e of (b.entry || [])) if (e.resource) out.push(e.resource); };
  collect(bundle);
  let guard = 0;
  let next = (bundle.link || []).find((l) => l.relation === 'next');
  while (next && next.url && guard < 50) {
    bundle = await medplum.get(next.url);
    collect(bundle);
    next = (bundle.link || []).find((l) => l.relation === 'next');
    guard += 1;
  }
  return out;
}

function tagVal(resource, system) {
  const t = ((resource.meta && resource.meta.tag) || []).find((x) => x.system === system);
  return t ? t.code : undefined;
}

// ── Rollups (MeasureReport, §2) ──────────────────────────────────────────────
function normalizeMeasureReport(mr) {
  const idn = (mr.identifier || []).find((i) => i.system === SYS.rollupId);
  let parts = idn && typeof idn.value === 'string' ? idn.value.split('|') : [];
  // [day, source, hospital, provider, product, metric, usecase, department, doctor]
  const g = (idx, fallbackSys) => (parts[idx] !== undefined ? parts[idx] : (tagVal(mr, fallbackSys) || '-'));
  const score = (mr.group && mr.group[0] && mr.group[0].measureScore) || {};
  const day = parts[0] || (mr.period && mr.period.start ? mr.period.start.slice(0, 10) : '');
  return {
    id: mr.id,
    day,
    source: g(1, SYS.source),
    hospital: g(2, SYS.hospital),
    provider: g(3, SYS.provider),
    product: g(4, SYS.product),
    metric: g(5, SYS.metric),
    usecase: g(6, SYS.usecase) || '-',
    department: g(7, SYS.department) || '-',
    doctor: g(8, SYS.doctor) || '-',
    value: Number(score.value) || 0,
    unit: score.unit || (parts[5] || ''),
    status: mr.status,
  };
}

export async function fetchRollups({ from, to }) {
  if (isMock()) {
    return mock().rollups.filter((r) => r.day >= from && r.day <= to);
  }
  // Two SEPARATE `period` params (AND semantics). Passing an array in the query
  // object serializes to one comma-joined value, which FHIR reads as OR and
  // Medplum rejects ("Invalid format for date search parameter" — hit live
  // 2026-07-19; mock mode never exercises this path).
  const rows = await searchAll(
    'MeasureReport',
    new URLSearchParams([['period', `ge${from}`], ['period', `le${to}`], ['_count', '200']]),
  );
  return rows.map(normalizeMeasureReport).filter((r) => r.day >= from && r.day <= to);
}

// ── Prices (ChargeItemDefinition, §3) ────────────────────────────────────────
function normalizeChargeItemDefinition(cid) {
  const url = cid.url || '';
  if (!url.startsWith(PRICE_URL_PREFIX)) return null;
  const priceKey = url.slice(PRICE_URL_PREFIX.length);
  const pc = (cid.propertyGroup && cid.propertyGroup[0] && cid.propertyGroup[0].priceComponent
    && cid.propertyGroup[0].priceComponent[0]) || {};
  const amount = pc.amount || {};
  const basisExt = (cid.extension || []).find((e) => e.url === PRICE_BASIS_EXT);
  return {
    id: cid.id,
    priceKey,
    title: cid.title || priceKey,
    amount: Number(amount.value),
    currency: amount.currency || 'USD',
    basis: basisExt ? basisExt.valueString : 'per-unit',
    effectiveFrom: (cid.effectivePeriod && cid.effectivePeriod.start) ? cid.effectivePeriod.start.slice(0, 10) : '1970-01-01',
    status: cid.status,
    unverified: false, // live entries edited/created are treated as confirmed
  };
}

// Returns { list, byKey } where byKey maps priceKey → entries sorted ascending by
// effectiveFrom (so effectivePrice can scan for the greatest ≤ day).
export async function fetchPrices() {
  let list;
  if (isMock()) {
    list = mock().prices.slice();
  } else {
    const rows = await searchAll('ChargeItemDefinition', { _count: 500 });
    list = rows.map(normalizeChargeItemDefinition).filter(Boolean).filter((p) => p.status !== 'retired');
  }
  return buildPriceIndex(list);
}

export function buildPriceIndex(list) {
  const byKey = new Map();
  for (const p of list) {
    if (!byKey.has(p.priceKey)) byKey.set(p.priceKey, []);
    byKey.get(p.priceKey).push(p);
  }
  for (const arr of byKey.values()) arr.sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1));
  return { list, byKey };
}

// Effective price for a priceKey on a day = greatest effectiveFrom ≤ day (§3).
export function effectivePrice(byKey, priceKey, day) {
  const arr = byKey.get(priceKey);
  if (!arr || !arr.length) return null;
  let chosen = null;
  for (const p of arr) { if (p.effectiveFrom <= day) chosen = p; else break; }
  return chosen;
}

// Cost of a rollup on its day. billed-cost is money as-is; quantity metrics convert
// by price-basis. No matching price → { priced:false } (never silently 0, §3).
export function computeCost(rollup, byKey) {
  if (rollup.metric === 'billed-cost') {
    return { amount: rollup.value, currency: rollup.unit || 'USD', priced: true, billed: true };
  }
  const priceKey = `${rollup.provider}:${rollup.product}:${rollup.metric}`;
  const p = effectivePrice(byKey, priceKey, rollup.day);
  if (!p || !Number.isFinite(p.amount)) return { amount: null, currency: null, priced: false, billed: false, priceKey };
  let amount;
  switch (p.basis) {
    case 'per-1m': amount = (rollup.value / 1e6) * p.amount; break;
    case 'per-1k': amount = (rollup.value / 1e3) * p.amount; break;
    case 'per-minute': amount = (rollup.value / 60) * p.amount; break; // value = audio-seconds
    case 'per-unit': default: amount = rollup.value * p.amount; break;
  }
  return { amount, currency: p.currency, priced: true, billed: false, priceKey };
}

// Enrich raw rollups with native + INR cost (same shape useOps produces). Used by
// screens that fetch a secondary window (e.g. previous-period deltas).
export function enrich(rollups, byKey, fxList) {
  return rollups.map((r) => {
    const cost = computeCost(r, byKey);
    const inr = cost.priced ? convertToINR(cost.amount, cost.currency, r.day, fxList) : null;
    return { ...r, cost, inr };
  });
}

// ── FX (Basic fx-rate, §4) ───────────────────────────────────────────────────
export function fxRateFor(fxList, base, quote, day) {
  let chosen = null;
  for (const f of fxList) {
    if (f.base !== base || f.quote !== quote) continue;
    if (f.effectiveFrom <= day && (!chosen || f.effectiveFrom > chosen.effectiveFrom)) chosen = f;
  }
  return chosen ? chosen.rate : null;
}

// Convert to INR for a day. Returns null when no rate exists (honest — screens keep
// the per-currency raw subtotal visible and flag the gap rather than inventing 0).
export function convertToINR(amount, currency, day, fxList) {
  if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) return null;
  if (currency === 'INR') return Number(amount);
  const rate = fxRateFor(fxList, currency, 'INR', day);
  if (rate === null) return null;
  return Number(amount) * rate;
}

// ── Basic-backed CRUD (telemetry read, manual-cost + fx-rate CRUD, §4) ───────
function parsePayloadBasic(res) {
  const ext = (res.extension || []).find((e) => e.url === PAYLOAD_EXT);
  let payload = {};
  try { payload = ext && ext.valueString ? JSON.parse(ext.valueString) : {}; } catch (_) { payload = {}; }
  return { id: res.id, ...payload };
}

function makeBasic(code, payload, existing) {
  const base = existing && existing.resourceType === 'Basic' ? { ...existing } : {
    resourceType: 'Basic',
    code: { coding: [{ system: OPS_CODE_SYSTEM, code }] },
  };
  base.code = { coding: [{ system: OPS_CODE_SYSTEM, code }] };
  const ext = (base.extension || []).filter((e) => e.url !== PAYLOAD_EXT);
  ext.push({ url: PAYLOAD_EXT, valueString: JSON.stringify(payload) });
  base.extension = ext;
  return base;
}

export async function fetchTelemetry({ from, to, kind } = {}) {
  let rows;
  if (isMock()) {
    rows = mock().telemetry.slice();
  } else {
    const res = await searchAll('Basic', { code: `${OPS_CODE_SYSTEM}|ops-telemetry`, _count: 500 });
    rows = res.map(parsePayloadBasic);
  }
  learnDoctorNames(rows); // ids → names, from call-site-stamped doctorName (§4)
  return rows.filter((r) => {
    const day = (r.at || '').slice(0, 10);
    if (from && day && day < from) return false;
    if (to && day && day > to) return false;
    if (kind && kind !== 'all' && r.kind !== kind) return false;
    return true;
  });
}

export async function fetchManualCosts() {
  if (isMock()) return mock().manualCosts.slice();
  const res = await searchAll('Basic', { code: `${OPS_CODE_SYSTEM}|manual-cost`, _count: 500 });
  return res.map(parsePayloadBasic);
}

export async function saveManualCost(item) {
  const payload = {
    label: item.label, category: item.category, amount: Number(item.amount),
    currency: item.currency, cadence: item.cadence, startDate: item.startDate || null,
    endDate: item.endDate || null, notes: item.notes || '',
  };
  if (isMock()) {
    const arr = mock().manualCosts;
    if (item.id) {
      const i = arr.findIndex((x) => x.id === item.id);
      if (i >= 0) arr[i] = { ...arr[i], ...payload };
      else arr.push({ id: item.id, ...payload });
    } else arr.push({ id: `mc-${Date.now()}`, ...payload });
    return { ok: true };
  }
  const medplum = await getClient();
  if (item.id) {
    const existing = await medplum.readResource('Basic', item.id);
    return medplum.updateResource(makeBasic('manual-cost', payload, existing));
  }
  return medplum.createResource(makeBasic('manual-cost', payload));
}

export async function deleteManualCost(id) {
  if (isMock()) {
    const arr = mock().manualCosts;
    const i = arr.findIndex((x) => x.id === id);
    if (i >= 0) arr.splice(i, 1);
    return { ok: true };
  }
  const medplum = await getClient();
  return medplum.deleteResource('Basic', id);
}

export async function fetchFxRates() {
  if (isMock()) return mock().fxRates.slice();
  const res = await searchAll('Basic', { code: `${OPS_CODE_SYSTEM}|fx-rate`, _count: 200 });
  return res.map(parsePayloadBasic);
}

export async function saveFxRate(item) {
  const payload = {
    base: item.base || 'USD', quote: item.quote || 'INR',
    rate: Number(item.rate), effectiveFrom: item.effectiveFrom,
  };
  if (isMock()) {
    mock().fxRates.push({ id: `fx-${Date.now()}`, ...payload });
    return { ok: true };
  }
  const medplum = await getClient();
  return medplum.createResource(makeBasic('fx-rate', payload));
}

// ── Price writes (§3: CREATE a new effective-dated entry, never mutate) ───────
export async function savePrice({ priceKey, title, amount, currency, basis, effectiveFrom }) {
  const cid = {
    resourceType: 'ChargeItemDefinition',
    url: `${PRICE_URL_PREFIX}${priceKey}`,
    status: 'active',
    title,
    effectivePeriod: { start: effectiveFrom },
    propertyGroup: [{ priceComponent: [{ type: 'base', amount: { value: Number(amount), currency } }] }],
    extension: [{ url: PRICE_BASIS_EXT, valueString: basis }],
  };
  if (isMock()) {
    mock().prices.push({
      id: `cid-${Date.now()}`, priceKey, title, amount: Number(amount),
      currency, basis, effectiveFrom, unverified: false,
    });
    return { ok: true };
  }
  const medplum = await getClient();
  return medplum.createResource(cid);
}

export async function seedDefaultPrices(defaults) {
  // Create an initial effective-dated ChargeItemDefinition for each default.
  const results = [];
  for (const d of defaults) {
    // eslint-disable-next-line no-await-in-loop
    await savePrice({ priceKey: d.priceKey, title: d.title, amount: d.amount, currency: d.currency, basis: d.basis, effectiveFrom: d.effectiveFrom });
    results.push(d.priceKey);
  }
  return results;
}

// ── Manual-cost monthly materialization (§4: recurring items per-month at read) ─
function monthKeysBetween(from, to) {
  const out = [];
  let y = Number(from.slice(0, 4)); let m = Number(from.slice(5, 7));
  const ey = Number(to.slice(0, 4)); const em = Number(to.slice(5, 7));
  let guard = 0;
  while ((y < ey || (y === ey && m <= em)) && guard < 400) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1; if (m > 12) { m = 1; y += 1; }
    guard += 1;
  }
  return out;
}

// Expand manual costs into per-month materialized occurrences within [from,to].
// monthly → full amount each active month; yearly → amount/12 amortized each active
// month; one-time → full amount in its start month only.
export function materializeManualCosts(items, { from, to }) {
  const months = monthKeysBetween(from.slice(0, 7) + '-01', to.slice(0, 7) + '-01');
  const occ = [];
  for (const it of items) {
    const startM = (it.startDate || from).slice(0, 7);
    const endM = it.endDate ? it.endDate.slice(0, 7) : null;
    for (const mk of months) {
      if (mk < startM) continue;
      if (endM && mk > endM) continue;
      let amount = null;
      if (it.cadence === 'one-time') { if (mk === startM) amount = Number(it.amount); }
      else if (it.cadence === 'yearly') amount = Number(it.amount) / 12;
      else amount = Number(it.amount); // monthly (default)
      if (amount === null) continue;
      occ.push({ item: it, monthKey: mk, amount, currency: it.currency });
    }
  }
  return occ;
}
